// The app-wide empty-value marker for the Stimulus layer (owner decision 5 on
// #15126, #15199): a cell or value a controller renders with nothing to show
// gets a MUTED EM DASH. This is the ONE place app/javascript types the
// character — controllers import it rather than hand-typing "--" or "—". Its
// twins are EmptyValueHelper::EMPTY_VALUE / `empty_value` (Rails, the same
// muted span) and EMPTY_VALUE in app/frontend/react/lib/emptyValue.ts (React);
// spec/helpers/empty_value_helper_spec.rb pins all three to one character.
//
// "N/A" is a different fact — the field does not apply to this record — and
// stays a literal where it means that.
import { escapeHtml } from "./html_sanitizer"

export const EMPTY_VALUE = "—"
// Shared muted utility (components/_utilities.scss), themed in both modes —
// the same class EmptyValueHelper::EMPTY_VALUE_CLASS puts on the span.
export const EMPTY_VALUE_CLASS = "gl-text-muted"

// Nothing to show: null, undefined, or a string that is empty once trimmed.
// 0 and false are values, never blank.
export function isBlankValue(value) {
  if (value === null || value === undefined) return true
  return typeof value === "string" && value.trim() === ""
}

// The muted em dash as markup, for template strings assigned to innerHTML.
export function emptyValueHtml() {
  return `<span class="${EMPTY_VALUE_CLASS}">${EMPTY_VALUE}</span>`
}

// `value` escaped for innerHTML, or the muted em dash when it is blank.
export function valueOrEmptyHtml(value) {
  return isBlankValue(value) ? emptyValueHtml() : escapeHtml(value)
}

// The muted em dash as a node, for DOM-building code.
export function emptyValueElement(doc = document) {
  const span = doc.createElement("span")
  span.className = EMPTY_VALUE_CLASS
  span.textContent = EMPTY_VALUE
  return span
}

// Writes `value` as text into `element`, or the muted em dash when it is blank.
// Text only — never parses `value` as markup.
export function setValueOrEmpty(element, value) {
  if (!element) return
  if (isBlankValue(value)) {
    element.replaceChildren(emptyValueElement(element.ownerDocument))
  } else {
    element.textContent = String(value)
  }
}
